// ./client/src/components/NotifyEta.js
import { useAppContext } from "../../context/appContext";
import useSmsTask from "../../hooks/useSmsTask";
import React, { useState } from "react";
import { Display1, Display } from "../workspace";
import ConfirmBox from "../ConfirmBox";

const formatEta = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;
  const suffix = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

const NotifyEta = ({
  customerPhone,
  enableTexting,
  contactName,
  jobNumber,
  pendingAppt,
  testMode = false,
}) => {
  const { job, customer, user } = useAppContext();
  const { status, error, sendSms } = useSmsTask();

  const [etaTime, setEtaTime] = useState("");
  const [showConfirmBox, setShowConfirmBox] = useState(false);

  const repName = user?.nickname || user?.full_name || "Your service rep";
  const messageText = `${repName} is on the way! ETA: ${formatEta(etaTime) || "shortly"}`;

  const pendingApptDisplay = pendingAppt
    ? `${pendingAppt.appt_date} ${formatEta(pendingAppt.appt_time) || ""}`.trim()
    : "No pending appointment";

  const confirmMessage = `Send this text message?\nTo: ${contactName || "customer"} (${customerPhone})\nJob: ${
    jobNumber || job?.job_number || "N/A"
  }\nNext pending appointment: ${pendingApptDisplay}`;

  const handleSend = () => {
    sendSms({
      customerPhone,
      text: messageText,
      jobId: job?.id,
      repId: repName,
      testMode,
    });
  };

  const handleConfirm = () => {
    setShowConfirmBox(true);
  };

  const isBusy = ["Queued", "Sending"].includes(status);
  const hideSection =
    !testMode && (!customerPhone || Number(user?.user_roles_id) > 1); // admin and developer only
  const textingDisabled = !testMode && customerPhone && !enableTexting;

  let feedback = "";
  if (isBusy) feedback = "Sending...";
  else if (status === "Delivered" || status === "Sent")
    feedback = "Customer notified!";
  else if (status === "Failed") feedback = error || "Failed to send";

  if (hideSection)
    return (
      <p className="text-red">
        Please select a contact with a phone number for the job and enable it
        for texting
      </p>
    );

  if (textingDisabled) {
    return (
      <div className="whole">
        <p className="text-red">
          Texting isn't enabled for the current contact
        </p>
      </div>
    );
  }

  return (
    <div className="flex-parent whole">
      <div className="one-fourth">
        <input
          type="time"
          className="form-input"
          value={etaTime}
          onChange={(e) => setEtaTime(e.target.value)}
          disabled={isBusy}
          aria-label="ETA"
        />
      </div>
      <div className="one-fourth btn-block">
        <button
          className="btn"
          type="button"
          onClick={handleConfirm}
          disabled={isBusy}
        >
          {isBusy ? "Sending..." : "Notify Customer"}
        </button>
      </div>
      <div className="whole">
        <Display name="Message Text" value={messageText} />
      </div>
      <div className="whole">
        {feedback && (
          <p className={status === "Failed" ? "text-red" : "text-green"}>
            {feedback}
          </p>
        )}
      </div>
      {/* ----- Confirmation Box ------ */}
      <div
        className={
          showConfirmBox ? "show-modal modal-container" : "modal-container"
        }
      >
        <div className="group">
          <ConfirmBox
            name="eta-notify-confirm"
            confirmedFunction={handleSend}
            message={confirmMessage}
            setShowFunction={setShowConfirmBox}
          />
        </div>
      </div>
    </div>
  );
};

export default NotifyEta;
